/**
 * Podium scene — end-of-game celebration. Top 5 ranked rows + confetti rain.
 *
 * The #1 row is rendered taller with a celebration-green background and a
 * crown emoji. Ranks 2-5 use compact rows with primary-themed accents. The
 * sparkles canvas drops a heavy confetti burst when the scene becomes visible.
 */

import type { PodiumRow } from '../state/types.js';
import { createSparkles, type SparklesHandle } from '../ui/sparkles.js';

export interface PodiumHandle {
  el: HTMLDivElement;
  setPodium(p: PodiumRow[]): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes an-podium-pop {
    0%   { opacity: 0; transform: translateY(20px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes an-podium-glow {
    0%, 100% { text-shadow: 0 8px 0 #03073D, 0 0 30px rgba(255,196,61,0.5); }
    50%      { text-shadow: 0 8px 0 #03073D, 0 0 90px rgba(255,196,61,1.0); }
  }
  .an-podium-wrap {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 80px;
    z-index: 30;
    color: #fff;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
  }
  .an-podium-title {
    font-size: 168px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 8px 0 #03073D;
    line-height: 0.9;
    animation: an-podium-glow 3.6s ease-in-out infinite;
  }
  .an-podium-sub {
    margin-top: 6px;
    font-size: 26px;
    letter-spacing: 0.30em;
    color: #94A3B8;
    text-transform: uppercase;
  }
  .an-podium-list {
    margin-top: 36px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 880px;
    max-width: 1080px;
  }
  .an-podium-row {
    display: grid;
    grid-template-columns: 96px 1fr auto;
    gap: 18px;
    align-items: center;
    padding: 14px 26px;
    border-radius: 14px;
    background: rgba(6,12,233,0.45);
    border: 2px solid var(--sg-primary, #FFC43D);
    color: #E2E8F0;
    font-size: 36px;
    letter-spacing: 0.04em;
    animation: an-podium-pop 360ms cubic-bezier(.18,.85,.4,1.0) both;
  }
  .an-podium-row .an-rank {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    text-align: center;
    font-size: 40px;
    color: var(--sg-primary, #FFC43D);
  }
  .an-podium-row .an-name {
    font-weight: 600;
    color: #FFFFFF;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .an-podium-row .an-pts {
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    font-variant-numeric: tabular-nums;
  }
  .an-podium-row.an-rank-1 {
    padding: 22px 32px;
    font-size: 60px;
    background: linear-gradient(180deg, #1F8A3D 0%, #155A28 100%);
    border-color: var(--sg-primary, #FFC43D);
    color: #FFFFFF;
    box-shadow: 0 0 60px rgba(34,197,94,0.55), 0 12px 30px rgba(0,0,0,0.55);
  }
  .an-podium-row.an-rank-1 .an-rank,
  .an-podium-row.an-rank-1 .an-pts { color: var(--sg-primary, #FFC43D); font-size: 64px; }
  .an-podium-row.an-rank-1 .an-name { color: #FFFFFF; }
  .an-podium-row.an-rank-1::before {
    content: '\\1F451'; /* crown */
    position: absolute;
  }
  .an-podium-row.an-rank-2 { background: linear-gradient(90deg, rgba(192,192,192,0.18), rgba(6,12,233,0.45)); }
  .an-podium-row.an-rank-3 { background: linear-gradient(90deg, rgba(205,127,50,0.18), rgba(6,12,233,0.45)); }
  .an-podium-row.an-rank-2 { animation-delay: 60ms; }
  .an-podium-row.an-rank-3 { animation-delay: 120ms; }
  .an-podium-row.an-rank-4 { animation-delay: 180ms; }
  .an-podium-row.an-rank-5 { animation-delay: 240ms; }
  .an-podium-empty {
    margin-top: 28px;
    font-size: 28px;
    color: #94A3B8;
    text-align: center;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-podium-style')) return;
  const s = document.createElement('style');
  s.id = 'an-podium-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function rowMarkup(r: PodiumRow): string {
  const rankClass = `an-rank-${Math.min(5, r.rank)}`;
  const crown = r.rank === 1 ? '\u{1F451} ' : '';
  return `
    <div class="an-podium-row ${rankClass}" data-rank="${r.rank}">
      <div class="an-rank">${crown}#${r.rank}</div>
      <div class="an-name"></div>
      <div class="an-pts">${r.total_points.toLocaleString()} pts</div>
    </div>
  `;
}

export function createPodium(stage: HTMLElement): PodiumHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'an-podium-wrap';
  el.setAttribute('data-scene', 'podium');
  el.setAttribute('data-testid', 'an-podium');
  el.innerHTML = `
    <div class="an-podium-title">GAME OVER</div>
    <div class="an-podium-sub">final standings</div>
    <div class="an-podium-list" data-testid="an-podium-list" data-role="list"></div>
  `;
  stage.appendChild(el);

  const list = el.querySelector<HTMLDivElement>('[data-role="list"]')!;
  const sparkles: SparklesHandle = createSparkles(el);
  let lastVisible = false;

  return {
    el,
    setPodium(rows) {
      if (rows.length === 0) {
        list.innerHTML = '<div class="an-podium-empty">No scores this game.</div>';
        return;
      }
      list.innerHTML = rows.slice(0, 5).map(rowMarkup).join('');
      // Fill in display names safely (avoid HTML injection).
      const items = list.querySelectorAll<HTMLDivElement>('.an-podium-row');
      rows.slice(0, 5).forEach((r, i) => {
        const nameEl = items[i]?.querySelector<HTMLDivElement>('.an-name');
        if (nameEl) nameEl.textContent = r.display_name;
      });
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
      if (v && !lastVisible) {
        sparkles.confetti({ count: 180, spread: 0.7 });
        // Second wave a beat later.
        setTimeout(() => sparkles.confetti({ count: 120, spread: 0.5 }), 700);
      }
      lastVisible = v;
    },
  };
}
